import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import API from "../../utils/api";
import { useAuth } from "../../App";
import {
  centersForLocation,
  getCenterValidationMessage,
  getTravelWindow,
  uniqueLocations,
} from "./oetAdvanceModule";

// ── Request Advance Modal ─────────────────────────────────────────────────────
const STEPS = ["Trip Details", "Expense Projection", "Advance Amount", "Confirm & Submit"];

const PROJECTION_CATEGORIES = [
  { key: "Travel",        icon: "🚂", hint: "Train, cab, auto, flights" },
  { key: "Hotel",         icon: "🏨", hint: "Accommodation per night" },
  { key: "Food",          icon: "🍽️", hint: "Meals during travel" },
  { key: "Fuel",          icon: "⛽", hint: "Fuel for own vehicle" },
  { key: "Courier",       icon: "📦", hint: "Courier / postal charges" },
  { key: "Stationeries",  icon: "📝", hint: "Stationery / printing" },
  { key: "Miscellaneous", icon: "📌", hint: "Any other expenses" },
];

// Extra categories shown only for OET Examination trips
const OET_PROJECTION_CATEGORIES = [
  { key: "Interlocutor Conveyance", icon: "🚕", hint: "Conveyance for interlocutors" },
  { key: "Freelancer Fee",          icon: "💼", hint: "Freelancer professional fee" },
  { key: "Freelancer Conveyance",   icon: "🚌", hint: "Freelancer conveyance" },
  { key: "Supporting Staff",        icon: "🦺", hint: "Supporting staff expenses" },
];

const EBEK_TRIP_OPTIONS = [
  "OET Examination",
  "Cambridge Examination",
  "Other Examination",
  "Marketing",
  "Petty Cash",
  "Others",
];

const EBEKMED_TRIP_OPTIONS = [
  "INC Training",
  "OSCE Training",
  "TOT Training",
  "Other Training",
  "Institution Visit",
  "Petty Cash",
  "Others",
];

// Ebekmed trip types that require training-specific fields
const EBEKMED_TRAINING_TYPES = ["INC Training", "OSCE Training", "TOT Training", "Other Training"];

// An advance keeps the original request for audit purposes, but once a manager
// approves or Finance credits a different amount, that effective amount is what
// the employee has actually received and what the dashboard must lead with.
const effectiveAdvanceAmount = (request) => {
  if (request?.status === "credited" && request.credited_amount != null) {
    return Number(request.credited_amount);
  }
  if (["approved", "credited"].includes(request?.status) && request.approved_amount != null) {
    return Number(request.approved_amount);
  }
  return Number(request?.amount || 0);
};

// Reusable projection category row
function ProjCatRow({ cat, projections, setProjections, oet }) {
  const val = parseFloat(projections[cat.key] || 0);
  const filled = val > 0;
  const bg     = filled ? (oet ? "#FFF7ED" : "#F0FDF4") : "#F9FAFB";
  const border = filled ? (oet ? "#FCD34D" : "#86EFAC") : "#E5E7EB";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, background: bg, border: `1px solid ${border}`, borderRadius: 10, padding: "10px 14px", transition: "all 0.2s" }}>
      <span style={{ fontSize: 22, flexShrink: 0 }}>{cat.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>{cat.key}</div>
        <div style={{ fontSize: 11, color: "#9ca3af" }}>{cat.hint}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 600 }}>₹</span>
        <input
          type="number" min="0" step="100" placeholder="0"
          value={projections[cat.key]}
          onChange={e => setProjections(p => ({ ...p, [cat.key]: e.target.value }))}
          style={{ width: 100, padding: "7px 10px", borderRadius: 8, border: "1.5px solid #D1D5DB", fontSize: 14, fontWeight: 600, textAlign: "right", fontFamily: "inherit", background: "white", outline: "none" }}
        />
      </div>
    </div>
  );
}

function RequestAdvanceModal({ onClose, onSuccess, accentColor, isEbek, currentUser }) {
  const accent = accentColor || "#E31E24";
  const [step, setStep]     = useState(1);
  const [loading, setLoading] = useState(false);
  const [projections, setProjections] = useState({
    Travel: "", Hotel: "", Food: "", Fuel: "", Courier: "", Stationeries: "", Miscellaneous: "",
    "Interlocutor Conveyance": "", "Freelancer Fee": "", "Freelancer Conveyance": "", "Supporting Staff": "",
  });
  const [titleOption, setTitleOption] = useState("");
  const [carryForward, setCarryForward] = useState(0);
  const [companyOwed, setCompanyOwed] = useState(0);
  const [oetCenters, setOetCenters] = useState([]);
  const [declarationMode, setDeclarationMode] = useState("now"); // "now" | "later"
  // Auto-detected center for this VM/AVM (populated after centers load)
  const [myCenter, setMyCenter] = useState(null);

  useEffect(() => {
    API.get("/advance-requests/my-carry-forward")
      .then(r => setCarryForward(parseFloat(r.data.carry_forward_balance || 0)))
      .catch(() => {});
    API.get("/advances/my-company-owed")
      .then(r => setCompanyOwed(parseFloat(r.data.balance || 0)))
      .catch(() => {});
    if (isEbek) {
      API.get("/advance-requests/oet-centers")
        .then(r => {
          const centers = r.data || [];
          setOetCenters(centers);
          // Auto-detect if this user is a VM or AVM
          const userEmail = (currentUser?.email || "").toLowerCase();
          const matched = centers.find(c =>
            (c.venue_manager_email || "").toLowerCase() === userEmail ||
            (c.assistant_venue_manager_email || "").toLowerCase() === userEmail
          );
          if (matched) {
            setMyCenter(matched);
            // Auto-fill center fields
            setForm(f => ({
              ...f,
              oet_location: matched.location,
              oet_center_no: matched.center_no,
              oet_center_name: matched.center_name,
              oet_center_location: matched.location,
              destination: matched.location,
            }));
          }
        })
        .catch(() => {});
    }
  }, []);
  const [form, setForm]     = useState({
    title      : "",
    destination: "",
    start_date : "",
    end_date   : "",
    purpose    : "",
    amount     : "",
    notes      : "",
    oet_location: "",
    oet_center_no: "",
    oet_center_name: "",
    oet_center_location: "",
    oet_test_date: "",
    // OET staff declarations (for cross-check against admin bills)
    oet_candidate_count:    "",
    oet_interlocutor_count: "",
    oet_interlocutor_names: "",
    oet_freelancer_count:   "",
    oet_freelancer_names:   "",
    // Ebekmed training / visit / petty cash fields
    institution_name:  "",
    participant_count: "",
    oet_support_staff_count: "",
    oet_support_staff_names: "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const getTitle = () => {
    if (titleOption === "Others") return form.title;
    if (titleOption) return titleOption;
    return form.title;
  };

  const isOetAdvance = isEbek && titleOption === "OET Examination";
  const locationCenters = centersForLocation(oetCenters, form.oet_location);
  const selectedCenter = oetCenters.find(center => center.center_no === form.oet_center_no);
  const centerValidationMessage = isOetAdvance ? getCenterValidationMessage(selectedCenter) : "";

  const handleTripTypeChange = (value) => {
    setTitleOption(value);
    setForm(f => {
      // If switching to OET Examination AND this user is a VM/AVM,
      // restore their auto-detected center instead of clearing it.
      const isOet = value === "OET Examination";
      return {
        ...f,
        title: "",
        destination: isOet && myCenter ? myCenter.location : "",
        start_date: "",
        end_date: "",
        purpose: "",
        oet_location:      isOet && myCenter ? myCenter.location   : "",
        oet_center_no:     isOet && myCenter ? myCenter.center_no   : "",
        oet_center_name:   isOet && myCenter ? myCenter.center_name : "",
        oet_center_location: isOet && myCenter ? myCenter.location  : "",
        oet_test_date: "",
        oet_candidate_count: "",
        oet_interlocutor_count: "",
        oet_interlocutor_names: "",
        oet_freelancer_count: "",
        oet_freelancer_names: "",
        oet_support_staff_count: "",
        oet_support_staff_names: "",
        institution_name: "",
        participant_count: "",
      };
    });
  };

  const handleOetLocationChange = (location) => {
    setForm(f => ({
      ...f,
      oet_location: location,
      oet_center_no: "",
      oet_center_name: "",
      oet_center_location: location,
      destination: location,
      oet_test_date: "",
      start_date: "",
      end_date: "",
    }));
  };

  const handleOetCenterChange = (centerNo) => {
    const center = oetCenters.find(c => c.center_no === centerNo);
    setForm(f => ({
      ...f,
      oet_center_no: centerNo,
      oet_center_name: center?.center_name || "",
      oet_center_location: center?.location || f.oet_location,
      destination: center?.location || f.destination,
      oet_test_date: "",
      start_date: "",
      end_date: "",
    }));
  };

  const handleOetTestDateChange = (testDate) => {
    const travelWindow = getTravelWindow(testDate);
    setForm(f => ({
      ...f,
      oet_test_date: testDate,
      start_date: travelWindow.start_date,
      end_date: travelWindow.end_date,
    }));
  };

  // All applicable projection categories for the selected trip type
  const activeProjCategories = isOetAdvance
    ? [...PROJECTION_CATEGORIES, ...OET_PROJECTION_CATEGORIES]
    : PROJECTION_CATEGORIES;

  // Projection total — computed once, used in step 2, 3, and 4
  const projTotal = activeProjCategories.reduce((s, c) => s + parseFloat(projections[c.key] || 0), 0);
  const projRows  = activeProjCategories.filter(c => parseFloat(projections[c.key] || 0) > 0);

  // Work out exactly what's still missing on Step 1 so we can show a hint
  const step1Blocking = (() => {
    if (!isEbek) {
      // Ebekmed — dropdown-based validation
      if (!titleOption)                                  return "Select a Trip / Purpose to continue";
      if (titleOption === "Others" && !form.title)       return "Enter the trip description to continue";
      // Training trips need institution + location + date
      if (EBEKMED_TRAINING_TYPES.includes(titleOption)) {
        if (!form.institution_name)                      return "Enter the Institution Name to continue";
        if (!form.destination)                           return "Enter the Location to continue";
        if (!form.start_date)                            return "Select the Date of Training to continue";
      }
      // Institution Visit needs location
      if (titleOption === "Institution Visit" && !form.destination) return "Enter the Location to continue";
      // Petty Cash needs center name + location
      if (titleOption === "Petty Cash") {
        if (!form.institution_name)                      return "Enter the Center Name to continue";
        if (!form.destination)                           return "Enter the Location to continue";
      }
      // Others / general — just need start date
      if (!["Institution Visit", "Petty Cash", ...EBEKMED_TRAINING_TYPES].includes(titleOption) && !form.start_date)
        return "Select a Start Date to continue";
      return null;
    }
    // Ebek
    if (!titleOption)                                   return "Select a Trip / Purpose to continue";
    if (titleOption === "Others" && !form.title)        return "Enter the trip description to continue";
    // Petty Cash (Ebek) — requires center name + location
    if (titleOption === "Petty Cash") {
      if (!form.institution_name)                       return "Enter the Center Name to continue";
      if (!form.destination)                            return "Enter the Location to continue";
    }
    if (titleOption !== "Petty Cash" && !form.start_date) return "Select a Start Date to continue";
    if (isOetAdvance) {
      // For VM/AVM users the center is auto-populated — only ask non-VM users to select it
      if (!form.oet_center_no && !myCenter)             return "Select a Center Number to continue";
      if (!form.oet_test_date)                          return "Select a Test Date to continue";
      if (centerValidationMessage)                      return centerValidationMessage;
    }
    return null;
  })();

  const canNext1 = !step1Blocking;
  // Step 2 (projections) is always optional — user can proceed with all zeros
  const canNext2 = true;
  // Step 3 requires amount
  const canNext3 = form.amount && parseFloat(form.amount) > 0;

  // What the employee actually gets: Finance transfers the requested amount
  // (plus anything the company owed from an earlier trip); carry-forward is
  // added on top of it as spendable advance but is not paid out again.
  const requestedAmount = Math.max(0, parseFloat(form.amount) || 0);
  const balanceBreakdown = (carryForward > 0 || companyOwed > 0) && requestedAmount > 0 ? (
    <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 12, padding: "12px 14px", fontSize: 13, color: "#065F46" }}>
      <div style={{ fontWeight: 800, marginBottom: 6 }}>How your advance adds up</div>
      <div style={{ display: "flex", justifyContent: "space-between" }}><span>Advance requested</span><strong>₹{requestedAmount.toLocaleString("en-IN")}</strong></div>
      {carryForward > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>+ Carry-forward added</span><strong>₹{carryForward.toLocaleString("en-IN")}</strong></div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #86EFAC", marginTop: 4, paddingTop: 4 }}>
        <span>You can spend on this trip</span><strong>₹{(requestedAmount + carryForward).toLocaleString("en-IN")}</strong>
      </div>
      {companyOwed > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}><span>+ Owed to you from earlier overspend</span><strong>₹{companyOwed.toLocaleString("en-IN")}</strong></div>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span>Finance will transfer in total</span><strong>₹{(requestedAmount + companyOwed).toLocaleString("en-IN")}</strong></div>
        </>
      )}
      <div style={{ fontSize: 11.5, marginTop: 6, opacity: 0.85 }}>
        Final figures use your balances when Finance credits the advance.
      </div>
    </div>
  ) : null;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build projections array from ALL applicable categories for this trip type.
      // activeProjCategories already includes OET-specific categories only for OET trips,
      // so non-OET trips naturally exclude them.
      const projectionsArray = activeProjCategories
        .filter(c => parseFloat(projections[c.key] || 0) > 0)
        .map(c => ({ category: c.key, projected_amount: parseFloat(projections[c.key]), notes: null }));

      await API.post("/advance-requests", {
        ...form,
        title: getTitle(),
        oet_declaration_deferred: declarationMode === "later",
        projections: projectionsArray,
      });
      toast.success("Advance request submitted! Your manager has been notified.");
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: 16, boxSizing: "border-box",
    }}>
      <div style={{
        background: "white", borderRadius: 20, width: "100%", maxWidth: 480,
        boxShadow: "0 25px 60px rgba(0,0,0,0.3)", overflow: "hidden",
        maxHeight: "90vh", display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ background: accent, padding: "20px 24px", color: "white" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>💰 Request Advance</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>Step {step} of 4 — {STEPS[step - 1]}</div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 8, color: "white", width: 32, height: 32, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
          </div>
          {/* Progress bar */}
          <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
            {[1,2,3,4].map(n => (
              <div key={n} style={{
                flex: 1, height: 4, borderRadius: 4,
                background: n <= step ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.3)",
                transition: "background 0.3s",
              }} />
            ))}
          </div>
        </div>
        {companyOwed > 0 && (
          <div style={{ margin: "14px 18px 0", padding: "12px 14px", borderRadius: 12, background: "#ECFDF5", border: "1px solid #A7F3D0", color: "#065F46", fontSize: 13, lineHeight: 1.5 }}>
            <strong>₹{companyOwed.toLocaleString("en-IN", { minimumFractionDigits: 2 })} from a settled trip is due to you.</strong><br />
            Because “carry into the next advance” was selected, create the next trip below. Finance will transfer this amount <strong>in addition to</strong> the usable advance requested for the new trip. It will not reduce the new trip budget.<br />
            To fund the same trip instead, Finance must use <strong>Supplementary advance for this trip</strong> before that trip is settled.
          </div>
        )}

        {/* Body */}
        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>

          {/* ── Step 1: Trip Details ── */}
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Tell us about your upcoming trip so the manager can review it.
              </p>
              {/* Both Ebek and Ebekmed use a dropdown */}
              <div className="form-group">
                <label className="form-label">Trip / Purpose <span style={{ color: "#DC2626" }}>*</span></label>
                <select className="form-control" value={titleOption} onChange={e => handleTripTypeChange(e.target.value)} autoFocus>
                  <option value="">— Select category —</option>
                  {(isEbek ? EBEK_TRIP_OPTIONS : EBEKMED_TRIP_OPTIONS)
                    .filter(o => o !== "OET Examination" || myCenter !== null)
                    .map(o => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                </select>
                {titleOption === "Others" && (
                  <input
                    className="form-control"
                    style={{ marginTop: 8 }}
                    placeholder="Describe the trip purpose"
                    value={form.title}
                    onChange={e => set("title", e.target.value)}
                  />
                )}
              </div>
              {isOetAdvance ? (
                <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: 14, background: "#F9FAFB" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 12 }}>📅 OET Exam Details</div>

                  {/* ── VM/AVM: Test Date FIRST, center auto-populated ── */}
                  {myCenter ? (
                    <>
                      <div className="form-group">
                        <label className="form-label">Exam / Test Date <span style={{ color: "#DC2626" }}>*</span></label>
                        <select className="form-control" value={form.oet_test_date} onChange={e => handleOetTestDateChange(e.target.value)}>
                          <option value="">— Select exam date —</option>
                          {(myCenter.test_dates || []).map(date => (
                            <option key={date} value={date}>{new Date(`${date}T00:00:00`).toLocaleDateString("en-IN", { weekday:"long", day:"2-digit", month:"long", year:"numeric" })}</option>
                          ))}
                        </select>
                      </div>
                      {/* Auto-populated center card */}
                      <div style={{ padding:"10px 14px", borderRadius:8, background:"#EFF6FF", border:"1.5px solid #BFDBFE", fontSize:13, color:"#1e40af" }}>
                        <div style={{ fontWeight:700, marginBottom:4 }}>🏢 Your Assigned Center (auto-filled)</div>
                        <div><strong>Center:</strong> {myCenter.center_name} &nbsp;·&nbsp; <strong>Code:</strong> {myCenter.center_no}</div>
                        <div><strong>Location:</strong> {myCenter.location}</div>
                        {myCenter.venue_manager && <div><strong>Venue Manager:</strong> {myCenter.venue_manager}</div>}
                        {myCenter.assistant_venue_manager && <div><strong>Asst. VM:</strong> {myCenter.assistant_venue_manager}</div>}
                      </div>
                    </>
                  ) : (
                    /* ── Non-VM: original location + center selectors ── */
                    <>
                      <div className="form-group">
                        <label className="form-label">Location <span style={{ color: "#DC2626" }}>*</span></label>
                        <select className="form-control" value={form.oet_location} onChange={e => handleOetLocationChange(e.target.value)}>
                          <option value="">Select location</option>
                          {uniqueLocations(oetCenters).map(location => <option key={location} value={location}>{location}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Center No <span style={{ color: "#DC2626" }}>*</span></label>
                        <select className="form-control" value={form.oet_center_no} onChange={e => handleOetCenterChange(e.target.value)} disabled={!form.oet_location}>
                          <option value="">Select center no</option>
                          {locationCenters.map(center => <option key={center.center_no} value={center.center_no}>{center.center_no}</option>)}
                        </select>
                      </div>
                      {selectedCenter && (
                        <div style={{ padding:"10px 12px", borderRadius:8, background:"white", border:"1px solid #E5E7EB", fontSize:13, color:"#374151", marginBottom:12 }}>
                          <div><strong>Center Name:</strong> {selectedCenter.center_name}</div>
                          <div><strong>Location:</strong> {selectedCenter.location}</div>
                          <div><strong>Venue Manager:</strong> {selectedCenter.venue_manager || "Not assigned"}</div>
                          <div><strong>Asst. Venue Manager:</strong> {selectedCenter.assistant_venue_manager || "Not assigned"}</div>
                        </div>
                      )}
                      {centerValidationMessage && (
                        <div style={{ padding:"10px 12px", borderRadius:8, background:"#FEE2E2", border:"1px solid #FCA5A5", fontSize:13, color:"#991B1B", marginBottom:12 }}>
                          {centerValidationMessage}
                        </div>
                      )}
                      <div className="form-group" style={{ marginBottom:0 }}>
                        <label className="form-label">Test Date <span style={{ color: "#DC2626" }}>*</span></label>
                        <select className="form-control" value={form.oet_test_date} onChange={e => handleOetTestDateChange(e.target.value)} disabled={!selectedCenter || !!centerValidationMessage}>
                          <option value="">Select test date</option>
                          {(selectedCenter?.test_dates || []).map(date => (
                            <option key={date} value={date}>{new Date(`${date}T00:00:00`).toLocaleDateString("en-IN")}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Non-OET: just a destination field (shown for Ebek non-OET trips) */
                !isEbek ? null : (
                  <div className="form-group">
                    <label className="form-label">Destination</label>
                    <input className="form-control" placeholder="e.g. Chennai, Tamil Nadu" value={form.destination} onChange={e => set("destination", e.target.value)} />
                  </div>
                )
              )}

              {/* ── Ebekmed: Training-specific fields (INC / OSCE / TOT / Other Training) ── */}
              {!isEbek && EBEKMED_TRAINING_TYPES.includes(titleOption) && (
                <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: 14, background: "#F9FAFB" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 12 }}>🏫 Training Details</div>
                  <div className="form-group">
                    <label className="form-label">Institution Name <span style={{ color: "#DC2626" }}>*</span></label>
                    <input className="form-control" placeholder="e.g. Govt. Medical College, Chennai"
                      value={form.institution_name} onChange={e => set("institution_name", e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Location <span style={{ color: "#DC2626" }}>*</span></label>
                    <input className="form-control" placeholder="e.g. Chennai, Tamil Nadu"
                      value={form.destination} onChange={e => set("destination", e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Training <span style={{ color: "#DC2626" }}>*</span></label>
                    <input className="form-control" type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">No. of Participants</label>
                    <input className="form-control" type="number" min="0" placeholder="e.g. 30"
                      value={form.participant_count} onChange={e => set("participant_count", e.target.value)} />
                  </div>
                </div>
              )}

              {/* ── Ebekmed: Institution Visit (formerly Marketing) ── */}
              {!isEbek && titleOption === "Institution Visit" && (
                <div className="form-group">
                  <label className="form-label">Location <span style={{ color: "#DC2626" }}>*</span></label>
                  <input className="form-control" placeholder="e.g. College / Hospital name & city"
                    value={form.destination} onChange={e => set("destination", e.target.value)} />
                </div>
              )}

              {/* ── Petty Cash (both Ebek and Ebekmed) ── */}
              {titleOption === "Petty Cash" && (
                <div style={{ border: "1px solid #E5E7EB", borderRadius: 12, padding: 14, background: "#F9FAFB" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#374151", marginBottom: 12 }}>🏢 Center Details</div>
                  <div className="form-group">
                    <label className="form-label">Center Name <span style={{ color: "#DC2626" }}>*</span></label>
                    <input className="form-control" placeholder="e.g. Ebek Med Training Center"
                      value={form.institution_name} onChange={e => set("institution_name", e.target.value)} />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Location <span style={{ color: "#DC2626" }}>*</span></label>
                    <input className="form-control" placeholder="e.g. Chennai, Tamil Nadu"
                      value={form.destination} onChange={e => set("destination", e.target.value)} />
                  </div>
                </div>
              )}

              {/* ── OET Staff Declaration (shown only for OET Examination) ── */}
              {isOetAdvance && form.oet_test_date && (
                <div style={{ border: "2px solid #FDE68A", borderRadius: 12, padding: 14, background: "#FFFBEB" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#92400E", marginBottom: 4 }}>
                    📋 Staff &amp; Candidate Declaration
                  </div>
                  <div style={{ fontSize: 12, color: "#78350F", marginBottom: 10 }}>
                    Finance cross-checks these numbers against the bills received. You can declare now or during expense submission.
                  </div>

                  {/* ── When to declare toggle ── */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
                    {[
                      { value:"now",   label:"📝 Declare Now",              sub:"I have the numbers" },
                      { value:"later", label:"🕐 During Expense Submission", sub:"I'll fill in later" },
                    ].map(opt => (
                      <button key={opt.value} type="button"
                        onClick={() => setDeclarationMode(opt.value)}
                        style={{
                          padding:"10px 12px", borderRadius:10, fontFamily:"inherit", cursor:"pointer", textAlign:"left",
                          border:`2px solid ${declarationMode===opt.value?"#92400e":"#FDE68A"}`,
                          background:declarationMode===opt.value?"#FEF3C7":"white",
                          color:declarationMode===opt.value?"#92400e":"#78350F",
                        }}>
                        <div style={{ fontWeight:700, fontSize:13 }}>{opt.label}</div>
                        <div style={{ fontSize:11, opacity:0.8, marginTop:2 }}>{opt.sub}</div>
                      </button>
                    ))}
                  </div>

                  {declarationMode === "later" ? (
                    <div style={{ padding:"10px 14px", background:"#FFF7ED", border:"1px solid #FDE68A", borderRadius:8, fontSize:12, color:"#92400e" }}>
                      ℹ️ You will be prompted to enter staff &amp; candidate numbers before adding your first expense. This is mandatory before expenses can be recorded.
                    </div>
                  ) : (
                  <><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">No. of Candidates <span style={{ color: "#DC2626" }}>*</span></label>
                      <input className="form-control" type="number" min="0" placeholder="e.g. 25"
                        value={form.oet_candidate_count} onChange={e => set("oet_candidate_count", e.target.value)} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">No. of Support Staff</label>
                      <input className="form-control" type="number" min="0" placeholder="e.g. 8"
                        value={form.oet_support_staff_count} onChange={e => set("oet_support_staff_count", e.target.value)} />
                    </div>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label className="form-label">No. of Interlocutors</label>
                      <input className="form-control" type="number" min="0" placeholder="e.g. 4"
                        value={form.oet_interlocutor_count} onChange={e => set("oet_interlocutor_count", e.target.value)} />
                    </div>
                    {parseInt(form.oet_interlocutor_count || 0) > 0 && (
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <label className="form-label">Interlocutor Names <span style={{ fontSize: 11, color: "#9ca3af" }}>(comma-separated)</span></label>
                        <textarea className="form-control" rows={2} placeholder="e.g. Dr. Anitha K, Mr. Rajan S, ..."
                          value={form.oet_interlocutor_names} onChange={e => set("oet_interlocutor_names", e.target.value)}
                          style={{ resize: "none" }} />
                      </div>
                    )}
                    <div className="form-group" style={{ marginBottom: 8 }}>
                      <label className="form-label">No. of Freelancers</label>
                      <input className="form-control" type="number" min="0" placeholder="e.g. 2"
                        value={form.oet_freelancer_count} onChange={e => set("oet_freelancer_count", e.target.value)} />
                    </div>
                    {parseInt(form.oet_freelancer_count || 0) > 0 && (
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Freelancer Names <span style={{ fontSize: 11, color: "#9ca3af" }}>(comma-separated)</span></label>
                        <textarea className="form-control" rows={2} placeholder="e.g. Suresh M, Priya N, ..."
                          value={form.oet_freelancer_names} onChange={e => set("oet_freelancer_names", e.target.value)}
                          style={{ resize: "none" }} />
                      </div>
                    )}
                    {parseInt(form.oet_support_staff_count || 0) > 0 && (
                      <div className="form-group" style={{ marginBottom: 0, marginTop: 8 }}>
                        <label className="form-label">Support Staff Names <span style={{ fontSize: 11, color: "#9ca3af" }}>(comma-separated)</span></label>
                        <textarea className="form-control" rows={2} placeholder="e.g. Ravi Kumar, Meena S, ..."
                          value={form.oet_support_staff_names} onChange={e => set("oet_support_staff_names", e.target.value)}
                          style={{ resize: "none" }} />
                      </div>
                    )}
                  </div>
                  </> )} {/* end declarationMode === "later" ? ... : ( */}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: window.innerWidth <= 480 ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Start Date <span style={{ color: "#DC2626" }}>*</span></label>
                  <input className="form-control" type="date" value={form.start_date} onChange={e => set("start_date", e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input className="form-control" type="date" value={form.end_date} min={form.start_date} onChange={e => set("end_date", e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Purpose / Reason for Travel</label>
                <textarea className="form-control" rows={2} placeholder="e.g. Meeting with client for project discussion" value={form.purpose} onChange={e => set("purpose", e.target.value)} style={{ resize: "vertical" }} />
              </div>
            </div>
          )}

          {/* ── Step 2: Expense Projection ── */}
          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Enter your expected spend per category. This helps your manager review your request and enables budget vs actual tracking.
              </p>

              {/* Category inputs */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Standard categories */}
                <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", paddingLeft: 2 }}>
                  {isOetAdvance ? "OET Travel, Stay & Food Budget" : "Travel Expenses"}
                </div>
                {PROJECTION_CATEGORIES.map(cat => (
                  <ProjCatRow key={cat.key} cat={cat} projections={projections} setProjections={setProjections} />
                ))}

                {/* OET-specific categories */}
                {isOetAdvance && (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", paddingLeft: 2, marginTop: 4 }}>
                      OET Staff &amp; Exam Charges
                    </div>
                    {OET_PROJECTION_CATEGORIES.map(cat => (
                      <ProjCatRow key={cat.key} cat={cat} projections={projections} setProjections={setProjections} oet />
                    ))}
                  </>
                )}
              </div>

              {/* Running total */}
              <div style={{
                background: projTotal > 0 ? `${accent}15` : "#F9FAFB",
                border: `2px solid ${projTotal > 0 ? accent + "50" : "#E5E7EB"}`,
                borderRadius: 10, padding: "12px 16px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>Projected Total</div>
                  {projTotal > 0 && (
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>Will be used as the suggested advance amount</div>
                  )}
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, color: projTotal > 0 ? accent : "#9ca3af" }}>
                  ₹{projTotal.toLocaleString("en-IN")}
                </div>
              </div>

              <div style={{ background: "#EFF6FF", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#1e40af" }}>
                💡 You can leave categories as ₹0 if they don't apply. You'll confirm the actual advance amount in the next step.
              </div>
            </div>
          )}

          {/* ── Step 3: Amount ── */}
          {step === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                How much advance do you need for this trip?
              </p>
              <div style={{ background: "#f9fafb", borderRadius: 12, padding: "14px 18px", border: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 13, color: "#6b7280" }}>📋 {getTitle()}</div>
                {form.destination && <div style={{ fontSize: 13, color: "#6b7280" }}>📍 {form.destination}</div>}
                <div style={{ fontSize: 13, color: "#6b7280" }}>📅 {form.start_date}{form.end_date && form.end_date !== form.start_date ? ` — ${form.end_date}` : ""}</div>
              </div>
              {carryForward > 0 && (
                <div style={{ background: "#D1FAE5", border: "1.5px solid #6EE7B7", borderRadius: 10, padding: "12px 14px", fontSize: 13 }}>
                  <div style={{ fontWeight: 800, color: "#065F46", marginBottom: 4 }}>💰 Carry-Forward Balance Available</div>
                  <div style={{ color: "#047857" }}>
                    You have <strong>₹{carryForward.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong> unspent from an earlier trip. It will be <strong>added on top</strong> of the advance Finance credits, so you can spend more than you request.
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Total Amount Needed (₹) <span style={{ color: "#DC2626" }}>*</span></label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  step="100"
                  placeholder="e.g. 5000"
                  value={form.amount}
                  onChange={e => set("amount", e.target.value)}
                  autoFocus
                  style={{ fontSize: 20, fontWeight: 700 }}
                />
                {form.amount && parseFloat(form.amount) > 0 && (
                  <div style={{ marginTop: 6, fontSize: 13, color: "#059669", fontWeight: 600 }}>
                    ₹{parseFloat(form.amount).toLocaleString("en-IN")} requested
                  </div>
                )}
                {form.amount && parseFloat(form.amount) > 0 && balanceBreakdown}
              </div>
              <div className="form-group">
                <label className="form-label">Additional Notes (optional)</label>
                <textarea className="form-control" rows={2} placeholder="e.g. Includes hotel for 2 nights + train tickets" value={form.notes} onChange={e => set("notes", e.target.value)} style={{ resize: "vertical" }} />
              </div>
            </div>
          )}

          {/* ── Step 4: Confirm ── */}
          {step === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>
                Please review your request before submitting.
              </p>
              <div style={{ background: "#f9fafb", borderRadius: 12, padding: 18, border: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", marginBottom: 12 }}>Request Summary</div>
                {[
                  { label: "Trip",          value: getTitle() },
                  form.institution_name && EBEKMED_TRAINING_TYPES.includes(titleOption)
                    ? { label: "Institution", value: form.institution_name }
                    : null,
                  form.institution_name && titleOption === "Petty Cash"
                    ? { label: "Center",    value: form.institution_name }
                    : null,
                  { label: "Location",      value: form.destination || "—" },
                  { label: "Date(s)",       value: `${form.start_date}${form.end_date && form.end_date !== form.start_date ? ` → ${form.end_date}` : ""}` },
                  form.participant_count && parseInt(form.participant_count) > 0
                    ? { label: "Participants", value: form.participant_count }
                    : null,
                  { label: "Purpose",       value: form.purpose || "—" },
                  { label: "Notes",         value: form.notes || "—" },
                ].filter(Boolean).map(row => (
                  <div key={row.label} style={{ display: "flex", gap: 12, marginBottom: 8 }}>
                    <div style={{ width: 90, fontSize: 13, color: "#9ca3af", flexShrink: 0 }}>{row.label}</div>
                    <div style={{ fontSize: 13, color: "#1f2937", fontWeight: 500 }}>{row.value}</div>
                  </div>
                ))}
              </div>

              {/* Projection summary */}
              {projRows.length > 0 && (
                <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#065F46", textTransform: "uppercase", marginBottom: 10 }}>📋 Expense Projection</div>
                  {projRows.map(c => (
                    <div key={c.key} style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 13 }}>
                      <span style={{ color: "#374151" }}>{c.icon} {c.key}</span>
                      <span style={{ fontWeight: 700, color: "#059669" }}>₹{parseFloat(projections[c.key] || 0).toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                  <div style={{ borderTop: "1px solid #86EFAC", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 800 }}>
                    <span style={{ color: "#065F46" }}>Projected Total</span>
                    <span style={{ color: "#059669" }}>₹{projTotal.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              )}

              {balanceBreakdown}

              <div style={{ background: `${accent}11`, border: `2px solid ${accent}44`, borderRadius: 12, padding: 18, textAlign: "center" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Advance Requested</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: accent, marginTop: 4 }}>
                  ₹{parseFloat(form.amount).toLocaleString("en-IN")}
                </div>
                {projTotal > 0 && Math.abs(parseFloat(form.amount) - projTotal) > 1 && (
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                    {parseFloat(form.amount) > projTotal
                      ? `₹${(parseFloat(form.amount) - projTotal).toLocaleString("en-IN")} above projection`
                      : `₹${(projTotal - parseFloat(form.amount)).toLocaleString("en-IN")} below projection`}
                  </div>
                )}
              </div>
              <div style={{ background: "#EFF6FF", borderRadius: 10, padding: 14, fontSize: 13, color: "#1e40af" }}>
                📧 Your manager will receive an email + push notification to approve or decline. You'll be notified once actioned.
              </div>
              <div style={{ background: "#FEF3C7", borderRadius: 10, padding: 14, fontSize: 13, color: "#92400e", border: "1px solid #FDE68A" }}>
                ⏳ <strong>Note:</strong> The advance will only be credited after 1 day from the time of your manager's approval.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #f3f4f6", display: "flex", flexDirection: "column", gap: 8 }}>
          {/* Step 1 blocking hint */}
          {step === 1 && step1Blocking && (
            <div style={{
              background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8,
              padding: "8px 14px", fontSize: 12, color: "#92400e", fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span>⚠️</span> {step1Blocking}
            </div>
          )}
          {/* Step 3 blocking hint */}
          {step === 3 && !canNext3 && (
            <div style={{
              background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8,
              padding: "8px 14px", fontSize: 12, color: "#92400e", fontWeight: 600,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span>⚠️</span> Enter the advance amount to continue
            </div>
          )}
          <div style={{ display: "flex", gap: 10 }}>
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} className="btn btn-secondary" style={{ flex: 1 }}>
              ← Back
            </button>
          )}
          {step < 4 && (
            <button
              onClick={() => {
                // When moving from step 2 (projection) to step 3 (amount), auto-suggest total
                if (step === 2 && projTotal > 0 && !form.amount) {
                  set("amount", String(Math.ceil(projTotal)));
                }
                setStep(s => s + 1);
              }}
              className="btn btn-primary"
              style={{
                flex: 2,
                background: (step === 1 && !canNext1) || (step === 3 && !canNext3) ? "#d1d5db" : accent,
                cursor: (step === 1 && !canNext1) || (step === 3 && !canNext3) ? "not-allowed" : "pointer",
              }}
              disabled={(step === 1 && !canNext1) || (step === 3 && !canNext3)}
            >
              {step === 2 ? "Next → Set Amount" : "Next →"}
            </button>
          )}
          {step === 4 && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn btn-primary"
              style={{ flex: 2, background: loading ? "#d1d5db" : accent, cursor: loading ? "not-allowed" : "pointer" }}
            >
              {loading ? "Submitting…" : "✅ Submit Request"}
            </button>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [trips, setTrips]                 = useState([]);
  const [myRequests, setMyRequests]       = useState([]);
  const [manualReminders, setManualReminders] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showAdvModal, setShowAdvModal]   = useState(false);
  const [cfBalance, setCfBalance]         = useState(0);
  const [cfLedger, setCfLedger]           = useState([]);
  const [showCfModal, setShowCfModal]     = useState(false);
  const [companyOwed, setCompanyOwed]     = useState(0);

  // Pick accent colour based on company
  const accent = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  const loadData = () => {
    API.get("/trips/my").then(r => { setTrips(r.data); setLoading(false); }).catch(() => setLoading(false));
    API.get("/advance-requests/my").then(r => setMyRequests(r.data)).catch(() => {});
    API.get("/trips/my/reminders").then(r => setManualReminders(r.data || [])).catch(() => {});
    API.get("/advances/my-carry-forward-ledger")
      .then(r => { setCfBalance(r.data.balance || 0); setCfLedger(r.data.ledger || []); })
      .catch(() => {});
    API.get("/advances/my-company-owed")
      .then(r => setCompanyOwed(Number(r.data.balance || 0)))
      .catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  // Arrive from a closed trip's "Request advance" button (/?requestAdvance=1)
  const [openRequestWhenReady, setOpenRequestWhenReady] = useState(
    () => new URLSearchParams(window.location.search).get("requestAdvance") === "1"
  );

  const activeTrips      = trips.filter(t => t.status === "active");
  const submittedTrips   = trips.filter(t => t.status === "submitted");
  const correctionTrips  = trips.filter(t => t.status === "correction");
  // Block if any trip is active (not yet submitted/settled) OR submitted (awaiting settlement).
  // A submitted trip is closed and stops blocking when it leaves a balance to carry
  // forward, or an overspend the company will pay with the next advance.
  const hasPendingBalance = (t) => {
    const balance = parseFloat(t.advance_amount || 0) - parseFloat(t.total_expenses || 0);
    return balance > 0.005 || (balance < -0.005 && t.overspend_resolution === "next_advance");
  };
  const blockingTrips  = [...submittedTrips.filter(t => !hasPendingBalance(t)), ...activeTrips];
  const totalAdvance   = trips.reduce((s, t) => s + parseFloat(t.advance_amount || 0), 0);
  const totalExpenses  = trips.reduce((s, t) => s + parseFloat(t.total_expenses || 0), 0);
  // Carry-forward stays in its own audit ledger until Finance applies it to a
  // trip.  Show the employee the combined funds they can currently use without
  // hiding either source of money.
  const activeTripAdvance = activeTrips.reduce((s, t) => s + parseFloat(t.advance_amount || 0), 0);
  const fundsAvailableNow = activeTripAdvance + cfBalance;

  const pendingRequests  = myRequests.filter(r => r.status === "pending");
  const approvedRequests = myRequests.filter(r => r.status === "approved");

  const handleRequestAdvance = () => {
    if (blockingTrips.length > 0) {
      const first = blockingTrips[0];
      const reason = first.status === "submitted"
        ? `"${first.title}" is submitted and awaiting settlement by Finance.`
        : parseFloat(first.advance_amount || 0) > 0
          ? `"${first.title}" has an active advance of ₹${parseFloat(first.advance_amount).toLocaleString("en-IN")} not yet settled.`
          : `"${first.title}" is an active trip that must be submitted and settled first.`;
      toast.error(`⚠️ Cannot request new advance — settle existing trip first. ${reason}`);
      return;
    }
    setShowAdvModal(true);
  };

  useEffect(() => {
    if (!openRequestWhenReady || loading) return;
    setOpenRequestWhenReady(false);
    window.history.replaceState(null, "", window.location.pathname);
    handleRequestAdvance();
  }, [openRequestWhenReady, loading]);

  const StatCard = ({ label, value, color, icon, border }) => {
    const valStr = String(value);
    const fontSize = valStr.length > 13 ? 13 : valStr.length > 10 ? 15 : valStr.length > 7 ? 18 : 22;
    return (
      <div className="stat-card" style={{ borderLeftColor: border || color }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
          <span style={{ fontSize: 24 }}>{icon}</span>
        </div>
        <div style={{ fontSize, fontWeight: 800, color: color || "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", lineHeight: 1.2 }}>{value}</div>
      </div>
    );
  };

  const STATUS_COLOR = { pending: "#d97706", approved: "#1d4ed8", declined: "#DC2626", credited: "#059669" };
  const STATUS_ICON  = { pending: "🕐", approved: "🏦", declined: "❌", credited: "💸" };
  const STATUS_LABEL = { pending: "Pending with Manager", approved: "Pending with Finance", declined: "Declined", credited: "Credited" };

  return (
    <div>
      {showAdvModal && (
        <RequestAdvanceModal
          accentColor={accent}
          onClose={() => setShowAdvModal(false)}
          onSuccess={loadData}
          isEbek={user?.company === "ebek"}
          currentUser={user}
        />
      )}

      {/* Welcome Banner */}
      <div className="page-banner" style={{
        background: user?.company === "ebekmed"
          ? "linear-gradient(135deg,#0a1628 0%,#134e4a 100%)"
          : "linear-gradient(135deg,#E31E24 0%,#B71C1C 100%)",
        borderRadius: 16, padding: "28px 32px", marginBottom: 28, color: "white",
        display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16,
        position: "relative", overflow: "hidden",
        animation: "exp-fade-up 0.36s ease both",
      }}>
        {/* Decorative radial glow */}
        <div style={{
          position: "absolute", top: -40, right: -40,
          width: 220, height: 220, borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          pointerEvents: "none",
        }} />
        <div style={{
          position: "absolute", bottom: -30, left: "30%",
          width: 160, height: 160, borderRadius: "50%",
          background: "rgba(255,255,255,0.05)",
          pointerEvents: "none",
        }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.7, marginBottom: 6 }}>
            Expense Manager
          </div>
          <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 6 }}>
            Good day, {user?.name?.split(" ")[0]}! 👋
          </div>
          <div style={{ fontSize: 14, opacity: 0.82 }}>
            Track your travel expenses and manage advances easily.
          </div>
        </div>
        <button
          onClick={handleRequestAdvance}
          style={{
            background: blockingTrips.length > 0 ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.18)",
            border: "2px solid rgba(255,255,255,0.38)",
            borderRadius: 12, color: "white", padding: "12px 22px",
            cursor: "pointer", fontSize: 15, fontWeight: 700,
            display: "flex", alignItems: "center", gap: 8, fontFamily: "inherit",
            transition: "background 0.2s, transform 0.16s, box-shadow 0.16s",
            opacity: blockingTrips.length > 0 ? 0.7 : 1,
            position: "relative", zIndex: 1,
          }}
          onMouseEnter={e => { if (!blockingTrips.length) { e.currentTarget.style.background = "rgba(255,255,255,0.28)"; e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.2)"; } }}
          onMouseLeave={e => { e.currentTarget.style.background = blockingTrips.length > 0 ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.18)"; e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
        >
          {blockingTrips.length > 0 ? "⚠️ Settle Previous Trip First" : "💰 Request Advance"}
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 14, marginBottom: 28 }}>
        <StatCard label="Total Trips"    value={trips.length}                                    icon="✈️" border="#6366f1" color="#4f46e5" />
        <StatCard label="Active Trips"   value={activeTrips.length}                              icon="🟢" border="#10b981" color="#059669" />
        <StatCard label="Total Advance"  value={`₹${totalAdvance.toLocaleString("en-IN")}`}     icon="💰" border="#f59e0b" color="#d97706" />
        <StatCard label="Available to Spend" value={`₹${fundsAvailableNow.toLocaleString("en-IN")}`} icon="✅" border="#059669" color="#047857" />
        <StatCard label="Total Spent"    value={`₹${totalExpenses.toLocaleString("en-IN")}`}    icon="📊" border={accent}  color={accent} />
      </div>

      {/* ── Carry Forward Balance Card ── */}
      {companyOwed > 0 && (
        <div style={{ background:"linear-gradient(135deg,#1E3A8A,#2563EB)", borderRadius:16, padding:"18px 22px", marginBottom:20, color:"white", boxShadow:"0 4px 18px rgba(37,99,235,.25)" }}>
          <div style={{ fontSize:12, fontWeight:800, opacity:.8, textTransform:"uppercase", letterSpacing:".07em" }}>Company owes you</div>
          <div style={{ fontSize:28, fontWeight:900, marginTop:4 }}>₹{companyOwed.toLocaleString("en-IN", { minimumFractionDigits:2 })}</div>
          <div style={{ fontSize:12, opacity:.85, marginTop:6, lineHeight:1.5 }}>Finance accepted this excess expense for your next advance. It will be paid in addition to the next trip's usable advance and will not reduce that trip's budget.</div>
        </div>
      )}
      {cfBalance > 0 && (
        <div style={{
          background: "linear-gradient(135deg, #065F46 0%, #047857 100%)",
          borderRadius: 16, padding: "20px 24px", marginBottom: 20,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 12, color: "white",
          boxShadow: "0 4px 20px rgba(5,150,105,0.3)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontSize: 36 }}>💰</div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
                Carry Forward Balance
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, letterSpacing: "-0.5px" }}>
                ₹{cfBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
                This amount is with you from a previous trip's excess advance. Finance will adjust this when issuing your next advance.
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
            <button onClick={() => setShowCfModal(true)}
              style={{ background: "rgba(255,255,255,0.15)", border: "1.5px solid rgba(255,255,255,0.4)", borderRadius: 10, color: "white", padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
              📋 View History
            </button>
            <div style={{ fontSize: 11, opacity: 0.7, textAlign: "right" }}>
              {cfLedger.length > 0 ? `${cfLedger.length} transaction${cfLedger.length > 1 ? "s" : ""}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* ── Carry Forward Ledger Modal ── */}
      {showCfModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}>
          <div style={{ background: "white", borderRadius: 20, width: "100%", maxWidth: 520, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 25px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "linear-gradient(135deg, #065F46, #047857)", padding: "20px 24px", color: "white" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>💰 Carry Forward History</div>
                  <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
                    Current balance: <strong>₹{cfBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</strong>
                  </div>
                </div>
                <button onClick={() => setShowCfModal(false)} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 8, color: "white", width: 32, height: 32, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit" }}>×</button>
              </div>
            </div>
            <div style={{ padding: 20, overflowY: "auto", flex: 1 }}>
              {cfLedger.length === 0 ? (
                <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                  <div>No transactions yet</div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {cfLedger.map((entry, i) => {
                    const isCredit = entry.type === "credit";
                    const amt = Math.abs(parseFloat(entry.amount));
                    return (
                      <div key={i} style={{ padding: "14px 16px", borderRadius: 12, border: `1.5px solid ${isCredit ? "#6EE7B7" : "#FCA5A5"}`, background: isCredit ? "#F0FDF4" : "#FFF7F7" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 18 }}>{isCredit ? "⬆️" : "⬇️"}</span>
                            <span style={{ fontWeight: 700, fontSize: 15, color: isCredit ? "#065F46" : "#991B1B" }}>
                              {isCredit ? "+" : "-"}₹{amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                          <span style={{ fontSize: 11, color: "#9ca3af" }}>
                            {new Date(entry.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                          </span>
                        </div>
                        {entry.reason && <div style={{ fontSize: 12, color: "#6b7280" }}>{entry.reason}</div>}
                        {entry.trip_title && <div style={{ fontSize: 12, color: "#4f46e5", marginTop: 4, fontWeight: 600 }}>✈️ {entry.trip_title}</div>}
                        {entry.actioned_by && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>By: {entry.actioned_by}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
              <div style={{ marginTop: 16, padding: "12px 16px", background: "#F3F4F6", borderRadius: 10, fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
                <strong style={{ color: "#374151" }}>How it works:</strong><br />
                When your advance is more than what you spend on a trip, the excess stays as carry-forward balance. Finance will apply this balance against your next advance — you only receive the difference as a bank transfer. To use this balance for a specific new trip, create the trip and inform Finance — they'll apply the carry-forward when issuing the advance.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unsettled Trips Warning */}
      {blockingTrips.length > 0 && (
        <div style={{ background: "#FEE2E2", border: "1px solid #FCA5A5", borderRadius: 12, padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span style={{ fontSize: 22, marginTop: 1 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: "#991b1b" }}>
              Cannot request new advance — previous trip not settled
            </div>
            <div style={{ fontSize: 13, color: "#b91c1c", marginTop: 3 }}>
              {blockingTrips[0]?.status === "submitted"
                ? `"${blockingTrips[0].title}" is submitted and waiting for Finance to settle.`
                : `"${blockingTrips[0]?.title}" has an active advance of ₹${parseFloat(blockingTrips[0]?.advance_amount||0).toLocaleString("en-IN")} not yet settled.`}
            </div>
            {blockingTrips.length > 1 && (
              <div style={{ fontSize: 12, color: "#dc2626", marginTop: 4 }}>
                Also unsettled: {blockingTrips.slice(1).map(t => `"${t.title}"`).join(", ")}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bill Correction Banner */}
      {correctionTrips.length > 0 && (
        <div style={{ background:"linear-gradient(135deg,#FEF3C7,#FDE68A)", border:"2px solid #F59E0B", borderRadius:12, padding:"16px 20px", marginBottom:16, display:"flex", alignItems:"flex-start", gap:14 }}>
          <span style={{ fontSize:28, flexShrink:0 }}>⚠️</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:800, color:"#92400e", fontSize:15 }}>
              Action Required: Bill Correction Requested
            </div>
            <div style={{ fontSize:13, color:"#78350f", marginTop:4 }}>
              Finance has flagged {correctionTrips.length === 1
                ? `"${correctionTrips[0].title}"`
                : `${correctionTrips.length} trips`} for bill correction.
              Please re-upload the correct bill(s) and resubmit.
            </div>
            <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
              {correctionTrips.map(t => (
                <Link key={t.id} to={`/trips/${t.id}`}
                  style={{ padding:"6px 14px", background:"#D97706", color:"white", borderRadius:8, fontSize:13, fontWeight:700, textDecoration:"none" }}>
                  Fix "{t.title}" →
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Finance Manual Reminder Banner */}
      {manualReminders.length > 0 && (
        <div style={{ background:"linear-gradient(135deg,#EFF6FF,#DBEAFE)", border:"2px solid #3B82F6", borderRadius:12, padding:"16px 20px", marginBottom:16, display:"flex", alignItems:"flex-start", gap:14 }}>
          <span style={{ fontSize:28, flexShrink:0 }}>🔔</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:800, color:"#1e40af", fontSize:15 }}>
              Finance has sent you a reminder
            </div>
            <div style={{ fontSize:13, color:"#1d4ed8", marginTop:4 }}>
              Please submit your expense bills for the following trip{manualReminders.length > 1 ? "s" : ""}:
            </div>
            <div style={{ display:"flex", gap:8, marginTop:10, flexWrap:"wrap" }}>
              {manualReminders.map(r => (
                <Link key={r.id} to={`/trips/${r.trip_id}`}
                  style={{ padding:"6px 14px", background:"#1D4ED8", color:"white", borderRadius:8, fontSize:13, fontWeight:700, textDecoration:"none" }}>
                  📋 {r.trip_title} →
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pending Advance Requests Banner */}
      {(pendingRequests.length > 0 || approvedRequests.length > 0) && (
        <div style={{ marginBottom: 24 }}>
          {pendingRequests.length > 0 && (
            <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 12, padding: "14px 18px", marginBottom: 10, display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 22 }}>🕐</span>
              <div>
                <div style={{ fontWeight: 700, color: "#92400e" }}>Advance request — Pending with Manager</div>
                <div style={{ fontSize: 13, color: "#78350f" }}>
                  ₹{parseFloat(pendingRequests[0].amount).toLocaleString("en-IN")} for "{pendingRequests[0].trip_title}" — waiting since {new Date(pendingRequests[0].requested_at).toLocaleDateString("en-IN")}
                </div>
              </div>
            </div>
          )}
          {approvedRequests.length > 0 && (
            <div style={{ background: "#D1FAE5", border: "1px solid #6EE7B7", borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 22 }}>✅</span>
              <div>
                <div style={{ fontWeight: 700, color: "#1e40af" }}>Advance approved — Pending with Finance</div>
                <div style={{ fontSize: 13, color: "#047857" }}>
                  ₹{effectiveAdvanceAmount(approvedRequests[0]).toLocaleString("en-IN")} for "{approvedRequests[0].trip_title}" — approved on {approvedRequests[0].approved_at ? new Date(approvedRequests[0].approved_at).toLocaleDateString("en-IN") : "—"}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: "flex", gap: 12, marginBottom: 28, flexWrap: "wrap" }}>
        <button onClick={handleRequestAdvance} className="btn btn-primary"
          disabled={blockingTrips.length > 0}
          style={{ fontSize: 14, background: blockingTrips.length > 0 ? "#9ca3af" : accent, cursor: blockingTrips.length > 0 ? "not-allowed" : "pointer" }}>
          {blockingTrips.length > 0 ? "⚠️ Settle Trip First" : "💰 Request Advance"}
        </button>
        <Link to="/trips" className="btn btn-secondary" style={{ fontSize: 14 }}>
          ✈️ My Trips
        </Link>
      </div>

      {/* My Advance Requests */}
      {myRequests.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1f2937" }}>My Advance Requests</h3>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {myRequests.slice(0, 5).map(req => {
              const hoursToApprove = req.hours_to_approve ? Math.round(req.hours_to_approve) : null;
              const hoursToCredit  = req.hours_to_credit  ? Math.round(req.hours_to_credit)  : null;
              const effectiveAmount = effectiveAdvanceAmount(req);
              const requestedAmount = Number(req.amount || 0);
              return (
                <div key={req.id} style={{ background: "#f9fafb", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: "#1f2937", fontSize: 14 }}>{req.trip_title}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                        📅 Requested {new Date(req.requested_at).toLocaleDateString("en-IN")}
                        {req.destination && ` · 📍 ${req.destination}`}
                      </div>
                      {/* Delay tracking */}
                      <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
                        {hoursToApprove !== null && (
                          <span style={{ fontSize: 11, background: "#EEF2FF", color: "#4f46e5", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>
                            ⏱ Approved in {hoursToApprove < 24 ? `${hoursToApprove}h` : `${Math.round(hoursToApprove/24)}d`}
                          </span>
                        )}
                        {hoursToCredit !== null && (
                          <span style={{ fontSize: 11, background: "#F0FDF4", color: "#059669", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>
                            💸 Credited in {hoursToCredit < 24 ? `${hoursToCredit}h` : `${Math.round(hoursToCredit/24)}d`}
                          </span>
                        )}
                        {req.status === "pending" && (
                          <span style={{ fontSize: 11, background: "#FEF3C7", color: "#92400e", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>
                            🕐 Waiting {Math.round(req.hours_pending || 0)}h for approval
                          </span>
                        )}
                      </div>
                      {req.manager_notes && (
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, fontStyle: "italic" }}>
                          Manager: "{req.manager_notes}"
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: STATUS_COLOR[req.status] || "#1f2937" }}>
                        ₹{effectiveAmount.toLocaleString("en-IN")}
                      </div>
                      {effectiveAmount !== requestedAmount && (
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
                          Requested ₹{requestedAmount.toLocaleString("en-IN")}
                        </div>
                      )}
                      <div style={{ fontSize: 12, fontWeight: 600, color: STATUS_COLOR[req.status], marginTop: 2 }}>
                        {STATUS_ICON[req.status]} {STATUS_LABEL[req.status] || req.status}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Trips */}
      <div className="card">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: "#1f2937" }}>Recent Trips</h3>
            <p style={{ fontSize: 13, color: "#9ca3af", marginTop: 2 }}>Your latest travel expense reports</p>
          </div>
          <Link to="/trips" className="btn btn-secondary btn-sm">View All →</Link>
        </div>

        {loading ? (
          <div style={{ padding: "20px 24px" }}>
            {[1,2,3].map(i => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 120px", gap: 12, padding: "14px 0", borderBottom: "1px solid var(--border-light)" }}>
                <div className="skeleton skeleton-text" style={{ width: "80%" }} />
                <div className="skeleton skeleton-text" style={{ width: "60%" }} />
                <div className="skeleton skeleton-text" style={{ width: "50%" }} />
                <div className="skeleton skeleton-text" style={{ width: "70%" }} />
              </div>
            ))}
          </div>
        ) : trips.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon" style={{ fontSize: 56, marginBottom: 14, display: "block" }}>✈️</div>
            <h3>No trips yet</h3>
            <p style={{ fontSize: 14 }}>Request an advance to start your first trip</p>
            <button onClick={() => setShowAdvModal(true)} className="btn btn-primary" style={{ marginTop: 16, background: accent }}>
              💰 Request Advance
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Trip</th>
                  <th>Destination</th>
                  <th>Dates</th>
                  <th>Advance</th>
                  <th>Spent</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {trips.slice(0, 5).map(trip => {
                  const balance = parseFloat(trip.advance_amount || 0) - parseFloat(trip.total_expenses || 0);
                  return (
                    <tr key={trip.id}>
                      <td><div style={{ fontWeight: 600, color: "#1f2937" }}>{trip.title}</div>
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>{trip.purpose}</div></td>
                      <td style={{ color: "#6b7280" }}>{trip.destination || "—"}</td>
                      <td style={{ fontSize: 13, color: "#6b7280" }}>
                        {new Date(trip.start_date).toLocaleDateString("en-IN")}
                        {trip.end_date && <> — {new Date(trip.end_date).toLocaleDateString("en-IN")}</>}
                      </td>
                      <td style={{ fontWeight: 600 }}>₹{parseFloat(trip.advance_amount||0).toLocaleString("en-IN")}</td>
                      <td style={{ fontWeight: 600, color: accent }}>₹{parseFloat(trip.total_expenses||0).toLocaleString("en-IN")}</td>
                      <td style={{ fontWeight: 700, color: balance >= 0 ? "#059669" : "#DC2626" }}>
                        {balance >= 0 ? "+" : ""}₹{Math.abs(balance).toLocaleString("en-IN")}
                      </td>
                      <td><span className={`badge badge-${trip.status}`}>{trip.status}</span></td>
                      <td><Link to={`/trips/${trip.id}`} className="btn btn-secondary btn-sm">View →</Link></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Version stamp — visible proof that latest build is running */}
      <div style={{ textAlign:"center", padding:"12px 0 4px", color:"#9ca3af", fontSize:11 }}>
        v2.5 · Expense Portal
      </div>
    </div>
  );
}
